import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';

const AuctionForm = ({ auction, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    item_id: '',
    start_price: '',
    reserve_price: '',
    bid_increment: '5.00',
    start_time: new Date(),
    end_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    status: 'draft',
  });

  useEffect(() => {
    fetchItems();

    if (auction) {
      setFormData({
        item_id: auction.item_id || '',
        start_price: auction.start_price || '',
        reserve_price: auction.reserve_price || '',
        bid_increment: auction.bid_increment || '5.00',
        start_time: auction.start_time ? new Date(auction.start_time) : new Date(),
        end_time: auction.end_time ? new Date(auction.end_time) : new Date(),
        status: auction.status || 'draft',
      });
    }
  }, [auction]);

  const fetchItems = async () => {
    try {
      const response = await api.get('/inventory?status=available');
      setItems(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        ...formData,
        start_time: formData.start_time.toISOString(),
        end_time: formData.end_time.toISOString(),
      };

      if (onSubmit) {
        await onSubmit(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save auction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              options={items}
              getOptionLabel={(option) => option.name || ''}
              value={items.find((i) => i.id === formData.item_id) || null}
              onChange={(e, newValue) => handleChange('item_id', newValue?.id || '')}
              renderInput={(params) => (
                <TextField {...params} label="Select Item" required />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Price"
              type="number"
              value={formData.start_price}
              onChange={(e) => handleChange('start_price', e.target.value)}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                step: '0.01',
                min: '0',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Reserve Price (Optional)"
              type="number"
              value={formData.reserve_price}
              onChange={(e) => handleChange('reserve_price', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                step: '0.01',
                min: '0',
              }}
              helperText="Minimum price to sell (hidden from bidders)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bid Increment"
              type="number"
              value={formData.bid_increment}
              onChange={(e) => handleChange('bid_increment', e.target.value)}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                step: '0.01',
                min: '0.01',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="ended">Ended</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="Start Time"
              value={formData.start_time}
              onChange={(value) => handleChange('start_time', value)}
              renderInput={(params) => <TextField {...params} fullWidth required />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="End Time"
              value={formData.end_time}
              onChange={(value) => handleChange('end_time', value)}
              renderInput={(params) => <TextField {...params} fullWidth required />}
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Saving...' : auction ? 'Update Auction' : 'Create Auction'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default AuctionForm;
